import { callWithRetries } from '@dtvgo/utils';

function createGetPlayerSchedules(middleware, { pageSize, maxAttempts, delayBetweenAttempts }) {
  const getNowNext = (channelId) => middleware.content.v2.getSchedulesByEventType(
    channelId,
    { assetToken: false, eventType: "now-next", pageSize }
  );
  const getSchedules = (channelId, startTime) => middleware.content.v2.getSchedules({
    startTime,
    channelId: [channelId],
    assetToken: false,
    pageSize
  });
  return async (channelId, { useNowNext = true, shouldRetry, startTime }) => {
    const fn = useNowNext ? getNowNext.bind(void 0, channelId) : getSchedules.bind(
      void 0,
      channelId,
      startTime
      // startTime is required when useNowNext is false
    );
    const { data: { channels: [channel] = [] } = {} } = shouldRetry ? await callWithRetries(fn, maxAttempts, delayBetweenAttempts) : await fn();
    return channel?.schedules?.map(normalizeSchedule) || [];
  };
}
function normalizeSchedule(schedule) {
  return {
    ...schedule,
    live: {
      ...schedule.live,
      startTime: new Date(schedule.live.startTime),
      endTime: new Date(schedule.live.endTime)
    }
  };
}
function createSchedulesCache(getPlayerSchedules) {
  let prevChannelId;
  let cache = [];
  const get = () => cache;
  const update = async (channelId, { useNowNext, hardUpdate, startTime }) => {
    const isNewChannel = channelId !== prevChannelId;
    prevChannelId = channelId;
    try {
      const newSchedules = await getPlayerSchedules(
        channelId,
        {
          useNowNext,
          shouldRetry: hardUpdate || isNewChannel,
          startTime
        }
      );
      if (newSchedules.length > 0 || hardUpdate || isNewChannel) {
        cache = newSchedules;
      }
    } catch (error) {
      console.error("[@dtvgo/player-utils>live>createSchedulesCache>update] error", error);
      if (hardUpdate || isNewChannel)
        cache = [];
    }
  };
  return { get, update };
}
function createUpdateSchedulesState(getChannelId, schedulesCache, setState, fakeScheduleDefaultDurationMinutes) {
  return async ({
    currentTimestamp,
    useNowNext,
    hardUpdate,
    startTime
  }) => {
    setState((currentState) => ({ ...currentState, isLoading: true }));
    await schedulesCache.update(getChannelId(), { useNowNext, hardUpdate, startTime });
    const schedules = schedulesCache.get();
    let current = schedules.find(
      (schedule) => schedule.live.startTime.getTime() <= currentTimestamp && currentTimestamp < schedule.live.endTime.getTime()
    );
    let next;
    if (current) {
      next = schedules.find((schedule) => schedule.live.startTime.getTime() === current?.live.endTime.getTime());
    } else {
      current = createFakeSchedule(schedules, currentTimestamp, fakeScheduleDefaultDurationMinutes);
    }
    setState({ schedules: { current, next }, isLoading: false });
  };
}
function createHandleLiveProgress(updateSchedulesState, getCurrentScheduleEndTime, isDelayedWindowSecs = 10) {
  let isUpdating = false;
  return (playbackTimestamp, liveEdgeTimestamp) => {
    const currentScheduleEndTime = getCurrentScheduleEndTime();
    if (!isUpdating && currentScheduleEndTime && playbackTimestamp > currentScheduleEndTime.getTime()) {
      isUpdating = true;
      updateSchedulesState({
        currentTimestamp: playbackTimestamp,
        useNowNext: Math.abs(liveEdgeTimestamp - playbackTimestamp) <= isDelayedWindowSecs * 1e3,
        startTime: currentScheduleEndTime,
        hardUpdate: false
      }).finally(() => {
        isUpdating = false;
      }).catch((error) => {
        console.error("[@dtvgo/player-utils>live>createHandleLiveProgress] error", error);
      });
    }
  };
}
function createFakeSchedule(schedules, currentTime, defaultDurationMinutes) {
  let nearestPreviousScheduleEndTime;
  let nearestNextScheduleStartTime;
  for (const { live: { startTime: startTime2, endTime: endTime2 } } of schedules) {
    if (endTime2.getTime() < currentTime && (!nearestPreviousScheduleEndTime || endTime2.getTime() > nearestPreviousScheduleEndTime.getTime())) {
      nearestPreviousScheduleEndTime = endTime2;
    } else if (startTime2.getTime() > currentTime && (!nearestNextScheduleStartTime || startTime2.getTime() < nearestNextScheduleStartTime.getTime())) {
      nearestNextScheduleStartTime = startTime2;
    }
  }
  const defaultDurationMillis = defaultDurationMinutes * 60 * 1e3;
  let startTime;
  if (nearestPreviousScheduleEndTime && nearestPreviousScheduleEndTime.getTime() - currentTime < defaultDurationMillis) {
    startTime = new Date(nearestPreviousScheduleEndTime);
  } else {
    startTime = new Date(currentTime);
    startTime.setSeconds(0);
    startTime.setMilliseconds(0);
  }
  const endTime = nearestNextScheduleStartTime && nearestNextScheduleStartTime.getTime() - startTime.getTime() < defaultDurationMillis ? new Date(nearestNextScheduleStartTime) : new Date(startTime.getTime() + defaultDurationMillis);
  return { live: { startTime, endTime } };
}

export { createFakeSchedule, createGetPlayerSchedules, createHandleLiveProgress, createSchedulesCache, createUpdateSchedulesState };
//# sourceMappingURL=live.js.map
